import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useModelStore } from '@stores/modelStore';
import { useInteractionManager } from 'src/interaction/useInteractionManager';
import { Grid } from '@components/Canvas/Grid/Grid';
import { Cursor } from 'src/components/Cursor/Cursor';
import { Nodes } from 'src/components/SceneLayers/Nodes/Nodes';
import { Rectangles } from 'src/components/SceneLayers/Rectangles/Rectangles';
import { Connectors } from 'src/components/SceneLayers/Connectors/Connectors';
import { ConnectorArrows } from 'src/components/SceneLayers/Connectors/ConnectorArrows';
import { ConnectorLabels } from 'src/components/SceneLayers/ConnectorLabels/ConnectorLabels';
import { ConnectorHandles } from 'src/components/SceneLayers/Connectors/ConnectorHandles';
import { TextBoxes } from 'src/components/SceneLayers/TextBoxes/TextBoxes';
import { SizeIndicator } from 'src/components/DebugUtils/SizeIndicator';
import { SceneLayer } from 'src/components/SceneLayer/SceneLayer';
import { TransformControlsManager } from 'src/components/TransformControlsManager/TransformControlsManager';
import { useScene } from 'src/hooks/useScene';
import { RenderLayer } from '@core/renderer/types';
import { RectangleLabels } from '@/components/SceneLayers/RectangleLabels';
import { IconReplacementHover } from '@/components/IconReplacement/IconReplacementHover';
import { IconPlacementPreview } from '../IconPlacementPreview/IconPlacementPreview';
import { SelectionHighlights } from '@/components/SceneLayers/SelectionHighlights/SelectionHighlights';
import { SelectionCountBadge } from '@/components/SelectionCountBadge/SelectionCountBadge';
import { GroupHighlights } from '@/components/SceneLayers/GroupHighlights/GroupHighlights';
import { InlineTextEditor } from '@/components/InlineTextEditor';
import { SelectionQuickActions } from '@/components/SelectionQuickActions';
import { LayoutActions } from '@/components/LayoutActions';
import { SmartGuides } from '@/components/SmartGuides';

export interface IsometricCanvasProps {
  showGrid?: boolean;
  backgroundColor?: string;
  enableInteractions?: boolean;
}

export const IsometricCanvas: React.FC<IsometricCanvasProps> = ({
  showGrid = true,
  backgroundColor,
  enableInteractions = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const interactionsRef = useRef<HTMLDivElement>(null);
  const renderCountRef = useRef(0);

  // UI State
  const enableDebugTools = useUiStateStore((state) => state.enableDebugTools);
  const mode = useUiStateStore((state) => state.mode);
  const uiStateActions = useUiStateStore((state) => state.actions);

  // Interaction management
  const { setInteractionsElement } = useInteractionManager(enableInteractions);

  // Scene data
  const { items, rectangles, connectors, textBoxes } = useScene();

  // Watch model items for changes (especially color changes)
  const modelItems = useModelStore((state) => state.items);

  // Filter out hidden layers and sort by zIndex for proper rendering order
  // Lower zIndex renders first (bottom), higher zIndex renders last (top)
  const visibleItems = items
    .filter((item) => item.visible !== false)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

  const visibleRectangles = rectangles
    .filter((rect) => rect.visible !== false)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

  const visibleConnectors = connectors
    .filter((conn) => conn.visible !== false)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

  const visibleTextBoxes = textBoxes
    .filter((textBox) => textBox.visible !== false)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

  // Initialize renderer and interaction elements
  useEffect(() => {
    if (!containerRef.current || !interactionsRef.current) return;

    setInteractionsElement(interactionsRef.current);
    uiStateActions.setRendererEl(containerRef.current);
  }, [setInteractionsElement, uiStateActions]);

  // Log when model items change (for debugging)
  useEffect(() => {
    renderCountRef.current += 1;
    // React will automatically re-render child components when modelItems changes
    // No need to force re-mount with key prop
  }, [modelItems]);

  return (
    <Box
      data-testid="isometric-canvas"
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        bgcolor: (theme) => {
          return backgroundColor ?? theme.customVars.customPalette.diagramBg;
        }
      }}
    >
      {/* Layer 0-1: Background elements (Rectangles) */}
      <SceneLayer order={RenderLayer.RECTANGLES}>
        <Rectangles rectangles={visibleRectangles} />
      </SceneLayer>
      {/* Layer 2: Grid */}
      {showGrid && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            zIndex: RenderLayer.GRID
          }}
        >
          <Grid />
        </Box>
      )}
      {/* Layer 3: Cursor */}
      {mode.showCursor && enableInteractions && (
        <SceneLayer order={RenderLayer.CURSOR}>
          <Box data-element="cursor">
            <Cursor />
          </Box>
        </SceneLayer>
      )}
      {/* Layer 4: Connectors */}
      <SceneLayer order={RenderLayer.CONNECTORS}>
        {/* connectors={connectors} */}
        <Connectors connectors={visibleConnectors} />
      </SceneLayer>
      {/* Layer 5: Text Boxes */}
      <SceneLayer order={RenderLayer.TEXT_BOXES}>
        <TextBoxes textBoxes={visibleTextBoxes} />
      </SceneLayer>
      {/* ✅ NEW: Rectangle Labels (same layer as connector labels) */}
      <SceneLayer order={RenderLayer.CONNECTOR_LABELS}>
        <RectangleLabels rectangles={visibleRectangles} />
      </SceneLayer>
      {/* Layer 7: Debug Tools */}
      {enableDebugTools && (
        <SceneLayer order={RenderLayer.DEBUG}>
          <SizeIndicator />
        </SceneLayer>
      )}
      {/* Layer 8: Interaction Layer (invisible, captures mouse events) */}
      <Box
        ref={interactionsRef}
        data-interaction-layer="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          // Keep the background hit surface below scene content so connectors
          // receive their own SVG pointer events and exact scene identity.
          zIndex: RenderLayer.CURSOR + 0.5,
          pointerEvents: enableInteractions ? 'auto' : 'none'
        }}
      />
      {/* Layer 9: Nodes - FIXED: No key prop, React re-renders naturally */}
      <SceneLayer order={RenderLayer.NODES}>
        <Nodes nodes={visibleItems} />
      </SceneLayer>
      {/* Selected connector handles must remain visible and interactive even
          when an endpoint occupies the same tile as a node. */}
      {enableInteractions && (
        <SceneLayer order={RenderLayer.TRANSFORM_CONTROLS + 1}>
          <ConnectorHandles connectors={visibleConnectors} />
        </SceneLayer>
      )}
      {/* Keep direction arrows readable when connected nodes are adjacent. */}
      <SceneLayer order={RenderLayer.NODES + 0.1}>
        <ConnectorArrows connectors={visibleConnectors} />
      </SceneLayer>
      {/* Connector descriptions must remain readable even when their path
          passes behind an isometric icon or node label. */}
      <SceneLayer order={RenderLayer.NODES + 0.2}>
        <ConnectorLabels connectors={visibleConnectors} />
      </SceneLayer>
      {/* Layer 10: Group highlights and selection */}
      {enableInteractions && (
        <>
          <GroupHighlights />
          <SelectionHighlights />
          <SelectionCountBadge />
          <InlineTextEditor />
          <SelectionQuickActions />
          <LayoutActions />
          <SmartGuides />
          <SceneLayer order={10.3}>
            <IconPlacementPreview />
          </SceneLayer>
          <SceneLayer order={10.5}>
            <IconReplacementHover />
          </SceneLayer>
          {/* Layer 11: Transform Controls (topmost) */}
          <SceneLayer order={RenderLayer.TRANSFORM_CONTROLS}>
            <Box data-element="transform-control">
              <TransformControlsManager />
            </Box>
          </SceneLayer>
        </>
      )}
    </Box>
  );
};
